import React, { useEffect, useState } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Paper,
  CircularProgress,
  Fade,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { CityService } from '../../services/city/CityService';
import { PlaceService } from '../../services/place/PlaceService';
import Theme from '../../theme/Theme';
import { PlaceEstabilishmentTypeRegisterDto } from '../../interfaces/PlaceEstabilishmentTypeRegister';
import { useSearchParams } from 'react-router-dom';
import FreeSpaceCard from '../../components/organism/FreeSpaceCard';
import { PlaceTypeEnum } from '../../enums/PlaceTypeEnum';
interface MediaDto {
  id: number;
  url: string;
}

interface EspacoDto {
  id: number;
  fantasyName: string;
  address: string;
  description?: string | null;
  logo?: MediaDto;
  banner?: MediaDto;
  medias?: MediaDto[];
  type?: PlaceTypeEnum;
  estabilishmentTypes: PlaceEstabilishmentTypeRegisterDto[];
}

const SearchPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState<number | undefined>(
    Number(searchParams.get('cidade')),
  );
  const [espacos, setEspacos] = useState<EspacoDto[]>([]);
  const [espacosFree, setEspacosFree] = useState<EspacoDto[]>([]);
  const [espacosPreRegistrados, setEspacosPreRegistrados] = useState<EspacoDto[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEspacos = (cityId?: number) => {
    setLoading(true);

    Promise.all([
      PlaceService.search({
        cityId: cityId ? cityId : undefined,
        random: true,
        types: [PlaceTypeEnum.PARTNER],
      }),
      PlaceService.search({
        cityId: cityId ? cityId : undefined,
        random: true,
        types: [PlaceTypeEnum.FREE],
      }),
      PlaceService.search({ cityId: cityId ? cityId : undefined, onlyFree: true }),
    ])
      .then(([espacosData, espacosFreeData, espacosPreRegistradosData]) => {
        setEspacos(espacosData || []);
        setEspacosFree(espacosFreeData || []);
        setEspacosPreRegistrados(espacosPreRegistradosData || []);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    CityService.search().then((resp) => {
      setCities(resp?.data || []);
    });

    const resizeObserver = new ResizeObserver(() => {
      window.parent.postMessage({ type: 'setHeight', height: document.body.scrollHeight }, '*');
    });

    resizeObserver.observe(document.body);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    setEspacos([]);
    setEspacosFree([]);
    setEspacosPreRegistrados([]);

    const params: any = {
      cidade: selectedCity ? selectedCity : undefined,
    };
    setSearchParams(params);

    fetchEspacos(selectedCity);
  }, [selectedCity]);

  const renderEspacos = (listaEspacos: EspacoDto[]) => (
    <Box
      my={10}
      display="flex"
      flexWrap="wrap"
      gap={5}
      justifyContent="flex-start"
      alignItems="stretch"
    >
      {listaEspacos.map((esp) => (
        <Box
          key={esp.id}
          sx={{
            width: {
              xs: '330px',
              sm: '600px',
              md: '27%',
              lg: '200px',
            },
            maxWidth: 600,
            px: 2,
          }}
        >
          <Card
            elevation={3}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: 6,
              },
              height: '100%',
              minHeight: { xs: 350, sm: 380, md: 420 },
            }}
          >
            <Box
              sx={{
                width: '100%',
                flexShrink: 0,
                '& .swiper-button-next, & .swiper-button-prev': {
                  color: 'orange',
                  textShadow: '0px 1px 5px gray',
                },
                '& .swiper-pagination-bullet': {
                  backgroundColor: 'orange',
                  textShadow: '0px 1px 5px gray',
                },
                '& .swiper-pagination-bullet-active': {
                  backgroundColor: 'darkorange',
                },
              }}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                cssMode={true}
                pagination={{ clickable: true }}
                slidesPerView={1}
              >
                {esp?.type === PlaceTypeEnum.FREE && esp?.logo && (
                  <SwiperSlide key={esp?.logo?.id}>
                    <CardMedia
                      component="img"
                      image={esp?.logo?.url}
                      alt={`${esp.fantasyName} - ${esp?.logo?.id}`}
                      sx={{
                        width: '100%',
                        height: 300,
                        objectFit: 'cover',
                      }}
                    />
                  </SwiperSlide>
                )}
                {esp?.banner && (
                  <SwiperSlide key={esp?.banner?.id}>
                    <CardMedia
                      component="img"
                      image={esp?.banner?.url}
                      alt={`${esp.fantasyName} - ${esp?.banner?.id}`}
                      sx={{
                        width: '100%',
                        height: 300,
                        objectFit: 'cover',
                      }}
                    />
                  </SwiperSlide>
                )}
                {(esp.medias || []).map((m) => (
                  <SwiperSlide key={m.id}>
                    <CardMedia
                      component="img"
                      image={m.url}
                      alt={`${esp.fantasyName} - ${m.id}`}
                      sx={{
                        width: '100%',
                        height: 300,
                        objectFit: 'cover',
                      }}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" gutterBottom color="orange" fontWeight="bold">
                <Link href={`/espaco/${esp.id}`} color="orange" underline="none" target="_blank">
                  {' '}
                  {esp.fantasyName}
                </Link>
              </Typography>
              {esp?.estabilishmentTypes.map((et) => (
                <Typography key={et.placeId} variant="subtitle1" color={Theme.palette.primary.main}>
                  {et.estabilishmentTypeName}
                </Typography>
              ))}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {esp.address}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  flexGrow: 1,
                  mt: 1,
                  color: 'text.secondary',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineBreak: 'anywhere',
                }}
              >
                {(esp.description || '').slice(0, 100)}
                {(esp.description || '').length > 100 ? '...' : ''}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      ))}
    </Box>
  );

  const renderFreeEspacos = (listaEspacos: EspacoDto[]) => (
    <Box display="grid" gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'} gap={2} my={4} p={2}>
      {listaEspacos.map((esp) => (
        <Card
          key={esp.id}
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 6,
            },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: 220 },
              flexShrink: 0,
              '& .swiper-button-next, & .swiper-button-prev': {
                color: 'orange',
                textShadow: '0px 1px 5px gray',
              },
              '& .swiper-pagination-bullet': { backgroundColor: 'orange' },
              '& .swiper-pagination-bullet-active': { backgroundColor: 'darkorange' },
            }}
          >
            <Swiper
              modules={[Navigation, Pagination]}
              navigation
              cssMode={true}
              pagination={{ clickable: true }}
              slidesPerView={1}
            >
              {esp?.logo && (
                <SwiperSlide key={esp.logo.id}>
                  <CardMedia
                    component="img"
                    image={esp.logo.url}
                    alt={`${esp.fantasyName} - logo`}
                    sx={{
                      width: '100%',
                      height: { xs: 200, sm: '100%' },
                      minHeight: 180,
                      objectFit: 'cover',
                    }}
                  />
                </SwiperSlide>
              )}
              {esp?.banner && (
                <SwiperSlide key={esp.banner.id}>
                  <CardMedia
                    component="img"
                    image={esp.banner.url}
                    alt={`${esp.fantasyName} - banner`}
                    sx={{
                      width: '100%',
                      height: { xs: 200, sm: '100%' },
                      minHeight: 180,
                      objectFit: 'cover',
                    }}
                  />
                </SwiperSlide>
              )}
              {(esp.medias || []).map((m) => (
                <SwiperSlide key={m.id}>
                  <CardMedia
                    component="img"
                    image={m.url}
                    alt={`${esp.fantasyName} - ${m.id}`}
                    sx={{
                      width: '100%',
                      height: { xs: 200, sm: '100%' },
                      minHeight: 180,
                      objectFit: 'cover',
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
          <CardContent
            sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1 }}
          >
            <Typography variant="h6" fontWeight="bold" color="orange">
              <Link href={`/espaco/${esp.id}`} color="orange" underline="none" target="_blank">
                {esp.fantasyName}
              </Link>
            </Typography>
            {esp.estabilishmentTypes.map((et) => (
              <Typography key={et.placeId} variant="subtitle2" color={Theme.palette.primary.main}>
                {et.estabilishmentTypeName}
              </Typography>
            ))}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {esp.address}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {(esp.description || '').slice(0, 150)}
              {(esp.description || '').length > 150 ? '...' : ''}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box
      display="flex"
      justifyContent="center"
      flexWrap="wrap"
      alignItems="center"
      sx={{
        mx: { xs: 2, sm: 2, md: 10, lg: 10 },
        my: 5,
        width: '93vw',
        height: 'auto',
        minHeight: 500,
        p: 0,
      }}
    >
      {/* Loading */}
      {loading && (
        <Box mt={5} display="flex" justifyContent="center" width="100%">
          <CircularProgress color="primary" />
        </Box>
      )}

      {(!!espacos.length || !!espacosFree.length || !!espacosPreRegistrados.length) && (
        <Fade
          in={
            !loading && (!!espacos.length || !!espacosFree.length || !!espacosPreRegistrados.length)
          }
          timeout={600}
        >
          <Box p={0} width="100%">
            {!!espacos.length && (
              <>
                <Box my={8} textAlign="center">
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color={Theme.palette.primary.main}
                    my={2}
                  >
                    Os Melhores Espaços Para Eventos em Um Só Lugar!
                  </Typography>
                </Box>
                {renderEspacos(espacos)}
              </>
            )}
            {!!espacosFree.length && (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    mb: isMobile ? 1.5 : 2,
                    my: 5,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">
                    Verifique também outros espaços disponíveis:
                  </Typography>
                </Paper>
                {renderFreeEspacos(espacosFree)}
              </>
            )}
            {!!espacosPreRegistrados.length && (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
                    gap: isMobile ? 1 : 1.5,
                  }}
                  mb={5}
                >
                  {espacosPreRegistrados.map((espaco) => (
                    <FreeSpaceCard key={espaco.id} {...(espaco as any)} />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default SearchPage;
